import ChangeApplicationStatus from '@/components/change-status'
import { EApplicationStatus, TApplication } from '@/interfaces'
import { ApplicationsState, AuthState, FormState } from '@/state'
import { applicationsStateActions } from '@/state/actions'
import { useComputed } from '@preact/signals-react'
import { Button, notification, Popconfirm, Row, Tooltip } from 'antd'
import { useMemo } from 'react'
import { FaEye } from 'react-icons/fa6'
import {
  MdDelete,
  MdEditSquare,
  MdOutlineWifiTetheringError,
} from 'react-icons/md'
import { hoverRow } from '.'
import { OfflineState } from '@/state/offline-management'
import { FiRefreshCw } from 'react-icons/fi'

export default function ApplicationTableActions({
  rowInfo,
}: {
  rowInfo: TApplication
}) {
  const formOpen = FormState.showForm.value

  const handleRemoveApplication = () => {
    ApplicationsState.loading.value = {
      ...ApplicationsState.loading.peek(),
      deletingRow: rowInfo.id as string,
    }
    applicationsStateActions
      .deleteApplication(rowInfo.id as string)
      .then(() =>
        notification.success({
          message: 'Solicitud eliminada',
          description: 'La solicitud ha sido eliminada correctamente',
          placement: 'top',
        })
      )
      .catch(() =>
        notification.error({
          message: 'Error eliminando la solicitud',
          description: 'Ocurrió un error eliminando la solicitud',
          placement: 'top',
        })
      )
      .finally(() => {
        ApplicationsState.loading.value = {
          ...ApplicationsState.loading.peek(),
          deletingRow: false,
        }
      })
  }

  const handleEditApplication = () => {
    applicationsStateActions
      .editApplication(rowInfo.id as string)
      .then((showMessage) => {
        if (showMessage) {
          notification.success({
            message: 'Solicitud cargada',
            description:
              'La información de la solicitud se ha cargado correctamente',
            placement: 'top',
          })
        }
      })
      .catch(() => {
        notification.error({
          message: 'Error cargando la solicitud',
          description:
            'La información de la solicitud no se ha obtenido correctamente',
          placement: 'top',
        })
      })
  }

  const handleViewApplication = () => {
    applicationsStateActions
      .viewApplication(rowInfo.id as string)
      .then((showMessage) => {
        if (showMessage) {
          notification.success({
            message: 'Solicitud cargada',
            description:
              'La información de la solicitud se ha cargado correctamente',
            placement: 'top',
          })
        }
      })
      .catch((error) => {
        console.error(error)
        notification.error({
          message: 'Error cargando la solicitud',
          description:
            'La información de la solicitud no se ha obtenido correctamente',
          placement: 'top',
        })
      })
  }

  const loadingDelete = useComputed(() => {
    const own = ApplicationsState.loading.value.deletingRow === rowInfo.id
    const other = Boolean(ApplicationsState.loading.value.deletingRow && !own)
    return {
      own,
      other,
      all: own || other,
    }
  })

  const loadingEdit = useComputed(() => {
    const own = ApplicationsState.loading.value.getById === rowInfo.id
    const other = Boolean(ApplicationsState.loading.value.getById && !own)

    return {
      own,
      other,
      all: own || other,
    }
  })

  const changingStatus = useComputed(() => {
    const own = ApplicationsState.loading.value.changeStatus.id === rowInfo.id
    const other = Boolean(
      ApplicationsState.loading.value.changeStatus.id && !own
    )
    return {
      own,
      other,
      all: own || other,
    }
  })

  const { canEdit, canDelete } = useMemo(() => {
    return {
      canEdit: [
        EApplicationStatus.WITHOUT_START,
        EApplicationStatus.IN_PROCESS,
        EApplicationStatus.COMPLETED,
        EApplicationStatus.CHANGES_REQUIRED,
        EApplicationStatus.OFFLINE,
      ].includes(rowInfo.estadoName as EApplicationStatus),
      canDelete: [
        EApplicationStatus.WITHOUT_START,
        EApplicationStatus.IN_PROCESS,
        EApplicationStatus.OFFLINE,
      ].includes(rowInfo.estadoName as EApplicationStatus),
    }
  }, [rowInfo.estadoName])

  const hideActions = useComputed(() =>
    Boolean(
      (OfflineState.isOnline.value && rowInfo.toSync) ||
        OfflineState.isSynchronizing.value
    )
  )

  const isSynching = useComputed(() =>
    OfflineState.syncAppsIds.value.includes(rowInfo.id as string)
  )

  const showEditBtn = useComputed(
    () =>
      AuthState.applicationPermission.value.edit &&
      !AuthState.statusChangeAllowed.value.some((statusOption) =>
        [
          EApplicationStatus.IN_REVIEW,
          EApplicationStatus.CHANGES_REQUIRED,
          EApplicationStatus.APPROVED,
          EApplicationStatus.REJECTED,
          EApplicationStatus.CORRECTLY_FILLED,
          EApplicationStatus.FINISHED,
        ].includes(statusOption)
      ) &&
      canEdit &&
      (!hideActions.value || !OfflineState.isSynchronizing.value) &&
      (!FormState.showForm.value ||
        !(
          (loadingEdit.value.own && ApplicationsState.viewMode.value.edit) ||
          (FormState.showForm.value && ApplicationsState.viewMode.value.edit) ||
          loadingEdit.value.other ||
          loadingDelete.value.all ||
          changingStatus.value.all
        ))
  )

  const showDeleteBtn = useComputed(
    () =>
      AuthState.applicationPermission.value.delete &&
      canDelete &&
      (!hideActions.value || !OfflineState.isSynchronizing.value) &&
      (!FormState.showForm.value ||
        !(
          loadingEdit.value.all ||
          changingStatus.value.all ||
          loadingDelete.value.other
        ))
  )

  const showViewBtn = useComputed(
    () =>
      AuthState.applicationPermission.value.read &&
      (!hideActions.value || !OfflineState.isSynchronizing.value) &&
      (!FormState.showForm.value ||
        !(
          (loadingEdit.value.own && ApplicationsState.viewMode.value.view) ||
          (FormState.showForm.value && ApplicationsState.viewMode.value.view) ||
          loadingEdit.value.other ||
          changingStatus.value.all ||
          loadingDelete.value.other
        ))
  )

  if (hoverRow.value !== rowInfo.id && !formOpen && !isSynching.value) {
    return null
  }

  return (
    <Row className={`table-actions`}>
      {isSynching.value && (
        <Tooltip title='Sincronizando información'>
          <FiRefreshCw size={20} className='sync-action' />
        </Tooltip>
      )}

      {(!hideActions.value || !OfflineState.isSynchronizing.value) && (
        <ChangeApplicationStatus
          application={rowInfo}
          loading={
            formOpen && (loadingEdit.value.all || loadingDelete.value.all)
          }
        />
      )}

      {rowInfo.toSync && (
        <Tooltip title='Registro no sincronizado'>
          <MdOutlineWifiTetheringError size={20} color='red' />
        </Tooltip>
      )}

      {showEditBtn.value && (
        <Tooltip placement='bottom' title='Editar'>
          <Button
            loading={loadingEdit.value.own}
            shape='circle'
            icon={<MdEditSquare />}
            onClick={handleEditApplication}
          />
        </Tooltip>
      )}

      {showDeleteBtn.value && (
        <Tooltip placement='bottom' title='Eliminar'>
          <Popconfirm
            title='¿Estás seguro de eliminar esta solicitud?'
            okText='Sí, eliminar'
            cancelText='No, cancelar'
            onConfirm={handleRemoveApplication}
            okButtonProps={{
              loading: loadingDelete.value.own,
            }}
            cancelButtonProps={{
              disabled: loadingDelete.value.own,
            }}
            placement='left'
          >
            <Button
              className='delete-btn'
              shape='circle'
              icon={<MdDelete />}
              loading={loadingDelete.value.own}
              disabled={!showDeleteBtn}
            />
          </Popconfirm>
        </Tooltip>
      )}

      {showViewBtn.value && (
        <Tooltip placement='bottom' title='Visualizar'>
          <Button
            loading={loadingEdit.value.own}
            shape='circle'
            icon={<FaEye />}
            onClick={handleViewApplication}
          />
        </Tooltip>
      )}
    </Row>
  )
}
